<template>
  <a
    v-if="data && href"
    :href="href"
    :class="[$style.config, $style.card]"
    :aria-label="data?.fields?.name || ''"
  >
    <div :class="$style.imageWrapper">
      <BasePicture
        v-if="computedImageUrl"
        :src="computedImageUrl"
        :alt="computedImageAlt"
        :class="$style.picture"
        :img-class="$style.image"
        loading="lazy"
      />
    </div>
    <div :class="$style.contentContainer">
      <header :class="$style.name">
        {{ computedTitle }}
      </header>
    </div>
  </a>
</template>

<script setup lang="ts">
import type { TypeProductWithoutUnresolvableLinksResponse } from '~/types/contentful'

const props = defineProps<{
  data: TypeProductWithoutUnresolvableLinksResponse | undefined
  href: string
}>()

const computedImageUrl = computed(() => {
  const imageUrl = props.data?.fields?.images?.[0]?.fields?.file?.url
  return imageUrl ? `${imageUrl}?q=90` : ''
})

const computedImageAlt = computed(() => {
  return (
    props.data?.fields?.images?.[0]?.fields?.description ||
    props.data?.fields?.name ||
    ''
  )
})

const computedTitle = computed(() => {
  return (
    props.data?.fields?.name || props.data?.fields?.type?.fields?.label || ''
  )
})
</script>

<style lang="scss" module>
.config {
}

.card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 232px;
  max-width: 100%;
  background-color: var(--white);
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
  color: inherit;
  cursor: pointer;
  min-height: 100%;
  overflow: hidden;
  text-decoration: none;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover,
  &:focus-visible {
    box-shadow: 0 16px 34px rgba(0, 0, 0, 0.12);
    color: inherit;
    text-decoration: none !important;
    transform: translateY(-2px);
  }

  &:visited,
  &:active {
    color: inherit;
    text-decoration: none !important;
  }
}

.imageWrapper {
  width: 100%;
  max-width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 20px 18px 12px;
  background:
    radial-gradient(circle at top, rgba(92, 102, 191, 0.08), transparent 60%),
    linear-gradient(180deg, #fff 0%, #fafafa 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.picture {
  width: 100%;
  height: 100%;
}

.image {
  height: 100% !important;
  object-fit: contain;
  width: 100%;
  filter: drop-shadow(0 10px 16px rgba(0, 0, 0, 0.14));
}

.contentContainer {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px 16px 18px;
  text-align: center;
}

.name {
  all: unset;
  display: block;
  font-family: var(--roboto-condensed);
  color: var(--purple-dark, #4b4c93);
  font-size: to-rem(18px);
  font-weight: 700;
  line-height: math.div(24, 18);
  max-width: 180px;
  text-align: center;
  text-decoration: none !important;
}

.card,
.card:link,
.card:visited,
.card:hover,
.card:active,
.card:focus,
.card:focus-visible {
  text-decoration: none !important;
}

.card * {
  text-decoration: none !important;
}
</style>
